"use client";
import { useState, useSyncExternalStore, type CSSProperties } from "react";
import { BlurhashCanvas } from "./BlurhashCanvas";
import type { AuthBackdrop as Photo } from "@/lib/auth-backdrop";

type ConnectionLike = { saveData?: boolean; effectiveType?: string };

/**
 * True when this visitor has asked for less data, or is on a connection
 * where a ~2560px photo would arrive long after the form it decorates:
 * `prefers-reduced-data`, the Save-Data switch, or a 2G-class link. Then the
 * page keeps the blurhash and never requests the photo at all.
 */
export function prefersLightData(
  nav: { connection?: ConnectionLike } = typeof navigator === "undefined" ? {} : (navigator as { connection?: ConnectionLike }),
  matches: (query: string) => boolean = (q) => typeof window !== "undefined" && window.matchMedia?.(q).matches === true,
): boolean {
  const c = nav.connection;
  if (c?.saveData) return true;
  if (c?.effectiveType === "slow-2g" || c?.effectiveType === "2g") return true;
  return matches("(prefers-reduced-data: reduce)");
}

const noSubscribe = () => () => {};

/**
 * Full-viewport photo behind the signed-out pages, painted in three layers
 * that each arrive later than the one before: the --ink ground (body), the
 * blurhash (a 32×32 canvas, on hydration), then the display-size photo,
 * fading in once decoded. The server snapshot is "no photo", so the bytes are
 * never requested before the data-saver check has run in the browser.
 *
 * The scrim is `::after` at `--auth-scrim` (lib/auth-backdrop.ts solves it).
 */
export function AuthBackdrop({ photo, scrim }: { photo: Photo; scrim: number }) {
  const wantPhoto = useSyncExternalStore(noSubscribe, () => !prefersLightData(), () => false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className="auth-backdrop" aria-hidden="true" style={{ "--auth-scrim": String(scrim) } as CSSProperties}>
      <BlurhashCanvas hash={photo.blurhash} className="auth-backdrop-hash" />
      {wantPhoto && !failed && (
        <img
          src={photo.src}
          width={photo.width}
          height={photo.height}
          alt=""
          decoding="async"
          fetchPriority="low"
          className={loaded ? "auth-backdrop-photo is-loaded" : "auth-backdrop-photo"}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
